import { useGetConversationSessionById } from '@/modules/gpt-chats/hooks/use-conversation-api';
import { ArrowLeft, MessagesSquare } from 'lucide-react';
import { parseAsString, useQueryStates, useQueryState } from 'nuqs';
import { useEffect, useMemo, useRef } from 'react';
import { Button } from '@/components/ui-kit/button';
import {
  AgentConversationSourceTrigger,
  AgentConversationDetailsSheet,
} from './agent-conversation-source';
import { MarkdownRenderer } from '@/modules/gpt-chats/components/markdown-renderer/markdown-renderer';
import { useProjectStore } from '@/modules/identifier/state/use-project-store';

import { parseBackendUtc } from '@/modules/gpt-chats/utils/date-utils';
import { formatFullDate } from '@/modules/gpt-chats/utils/date-utils';
import { useTranslation } from 'react-i18next';

type AgentConversationBoxProps = {
  agentId: string;
  title: string;
  username: string;
  isPrivate: boolean;
};

const EmptySession = () => {
  const { t } = useTranslation();
  return (
    <div className="w-[65%] flex-1 rounded-sm bg-background p-4">
      <div className="flex h-full flex-col items-center justify-center">
        <MessagesSquare className="h-12 w-12" />
        <p className="mt-3 text-lg font-medium text-high-emphasis">{t('SELECT_A_CONVERSATION')}</p>
      </div>
    </div>
  );
};

const Separator = () => <span className="mx-2">●</span>;

const formatResponseContent = (response: string): string => {
  if (typeof response === 'string') {
    try {
      const parsed = JSON.parse(response);
      if (typeof parsed === 'object' && parsed !== null) {
        return `:::json\n${response}\n:::`;
      }
      return response;
    } catch {
      return response;
    }
  }
  return String(response);
};

export const AgentConversationBox = ({
  agentId,
  title,
  username,
  isPrivate,
}: AgentConversationBoxProps) => {
  const { t } = useTranslation();
  const [queryParams, setQueryParams] = useQueryStates({
    widget_id: parseAsString.withDefault(''),
    session_id: parseAsString.withDefault(''),
  });

  const [detailsPanel, setDetailsPanel] = useQueryState('details_panel');

  const projectKey = useProjectStore().selectedProject?.tenantId || '';
  const scrollRef = useRef<HTMLDivElement>(null);

  const { data, isLoading } = useGetConversationSessionById({
    widget_id: queryParams?.widget_id,
    session_id: queryParams?.session_id,
    agent_id: agentId,
    limit: 100,
    offset: 0,
    project_key: projectKey,
    is_private: isPrivate,
    allow_created_by_filter: false,
    is_minimal: false,
  });

  const reversedSessions = useMemo(() => {
    const sessions = data?.sessions ?? [];

    return sessions
      .map((item) => ({
        ...item,
        Response: item.Response,
      }))
      .sort(
        (a, b) =>
          parseBackendUtc(a.QueryTimestamp).getTime() - parseBackendUtc(b.QueryTimestamp).getTime()
      );
  }, [data]);

  const selectedSession = useMemo(() => {
    return reversedSessions.find((s) => s._id === detailsPanel) || null;
  }, [reversedSessions, detailsPanel]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [reversedSessions]);

  if (!queryParams.session_id) return <EmptySession />;

  return (
    <div className="h-full min-h-[500px] min-w-[380px] flex-1 rounded-sm bg-background">
      <div className="flex items-center gap-2 border p-4 md:hidden">
        <Button
          type="button"
          variant="ghost"
          className="h-fit w-fit px-1 py-0.5"
          onClick={() => {
            setQueryParams((prev) => ({
              ...prev,
              session_id: '',
            }));
          }}
        >
          <ArrowLeft className="aspect-square w-4" />
        </Button>
        <div className="truncate">{title}</div>
      </div>
      {isLoading ? (
        <div className="flex h-full flex-col items-center justify-center text-medium-emphasis">
          {t('LOADING_CONVERSATION')}
        </div>
      ) : reversedSessions && reversedSessions.length > 0 ? (
        <div className="p-4">
          <div
            ref={scrollRef}
            className="flex max-h-[calc(100vh-300px)] flex-col gap-3 overflow-auto bg-background"
          >
            {reversedSessions.map((msg) => (
              <div key={msg?._id}>
                {msg?.Query?.trim() && (
                  <div className="flex justify-end">
                    <div className="flex max-w-[80%] flex-col items-end">
                      <div className="mb-1 text-xs font-semibold">
                        <time
                          className="mt-1 block text-xs text-low-emphasis"
                          dateTime={parseBackendUtc(msg?.QueryTimestamp).toISOString()}
                          title={parseBackendUtc(msg?.QueryTimestamp).toUTCString()}
                        >
                          <div className="flex flex-row">
                            <span>{username || t('YOU')}</span>
                            <Separator />
                            <span>
                              {formatFullDate(new Date(parseBackendUtc(msg?.QueryTimestamp)))}
                            </span>
                          </div>
                        </time>
                      </div>

                      <div className="mt-1 rounded-sm bg-surface-app p-4 text-sm text-medium-emphasis shadow-sm">
                        {msg.Query}
                      </div>
                    </div>
                  </div>
                )}

                {msg?.Response && (
                  <div
                    className={`mt-4 max-w-[85%] text-medium-emphasis ${msg?.Response?.startsWith('{') && msg?.Response?.endsWith('}') ? '' : 'w-fit'}`}
                  >
                    <div className="mb-1 flex items-center justify-between text-xs font-semibold">
                      <time
                        className="mr-1 mt-1 block text-left text-xs text-low-emphasis"
                        dateTime={parseBackendUtc(msg?.ResponseTimestamp).toISOString()}
                        title={parseBackendUtc(msg?.ResponseTimestamp).toUTCString()}
                      >
                        <div className="flex flex-row">
                          <p className="text-high-emphasis">{t('AGENT')}</p>
                          <Separator />

                          <p className="font-normal text-low-emphasis">
                            {formatFullDate(new Date(parseBackendUtc(msg?.ResponseTimestamp)))}
                          </p>
                        </div>
                      </time>
                      {msg?.WorkflowTrace && (
                        <AgentConversationSourceTrigger
                          onClick={() => setDetailsPanel(msg._id)}
                          isOpen={detailsPanel === msg._id}
                        />
                      )}
                    </div>
                    <div
                      className={`mt-1 rounded-sm bg-surface-app p-4 text-sm shadow-sm ${msg?.Response?.startsWith('{') && msg?.Response?.endsWith('}') ? '' : 'w-fit'}`}
                    >
                      <MarkdownRenderer content={formatResponseContent(msg.Response)} />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          <AgentConversationDetailsSheet
            session={selectedSession}
            isOpen={!!detailsPanel && !!selectedSession}
            onOpenChange={(open) => {
              if (!open) setDetailsPanel(null);
            }}
          />
        </div>
      ) : (
        <div className="flex h-full flex-col items-center justify-center text-medium-emphasis">
          <MessagesSquare className="h-12 w-12" />
          <p className="mt-3 text-lg font-medium">{t('NO_MESSAGES_IN_CONVERSATION')}</p>
        </div>
      )}
    </div>
  );
};
